/**
 * Custom hook for managing sample weeks state and loading
 * Used across Single Flight Pair, Multiple Flight Pairs, and Schedule Comparison pages
 */

import { useState, useEffect, useCallback } from 'react';
import { sampleWeekApi } from '../services/api';
import type { SampleWeek } from '../services/api';

export const SELECTED_SAMPLE_WEEK_SESSION_KEY = 'selectedSampleWeekId';

export const getPersistedSelectedSampleWeekId = (): string => {
  if (typeof window === 'undefined') return '';
  return sessionStorage.getItem(SELECTED_SAMPLE_WEEK_SESSION_KEY) || '';
};

export const persistSelectedSampleWeekId = (weekId: string): void => {
  if (typeof window === 'undefined') return;
  if (weekId) {
    sessionStorage.setItem(SELECTED_SAMPLE_WEEK_SESSION_KEY, weekId);
  } else {
    sessionStorage.removeItem(SELECTED_SAMPLE_WEEK_SESSION_KEY);
  }
};

export const useSampleWeeks = () => {
  const [sampleWeeks, setSampleWeeks] = useState<SampleWeek[]>([]);
  const [selectedSampleWeekState, setSelectedSampleWeekState] = useState<string>(
    () => getPersistedSelectedSampleWeekId()
  );
  const [isLoadingSampleWeeks, setIsLoadingSampleWeeks] = useState(false);

  const setSelectedSampleWeek = useCallback((weekId: string) => {
    setSelectedSampleWeekState(weekId);
    persistSelectedSampleWeekId(weekId);
  }, []);

  const loadSampleWeeks = async () => {
    setIsLoadingSampleWeeks(true);
    try {
      const weeks = await sampleWeekApi.list();
      setSampleWeeks(weeks);
      if (weeks.length === 0) {
        setSelectedSampleWeek('');
        return;
      }

      setSelectedSampleWeekState((currentSelected) => {
        const persistedSelected = getPersistedSelectedSampleWeekId();
        const preferredSelected = currentSelected || persistedSelected;

        if (preferredSelected && weeks.some((week) => week.id === preferredSelected)) {
          persistSelectedSampleWeekId(preferredSelected);
          return preferredSelected;
        }

        const fallbackWeekId = weeks[0].id;
        persistSelectedSampleWeekId(fallbackWeekId);
        return fallbackWeekId;
      });
    } catch (error) {
      console.error('Failed to load sample weeks:', error);
    } finally {
      setIsLoadingSampleWeeks(false);
    }
  };

  // Load sample weeks on mount
  useEffect(() => {
    loadSampleWeeks();
  }, []);

  useEffect(() => {
    if (!selectedSampleWeekState) return;
    if (!sampleWeeks.some((week) => week.id === selectedSampleWeekState)) return;

    void sampleWeekApi
      .warmNonstopService(selectedSampleWeekState)
      .catch((error) => {
        console.warn('Failed to warm non-stop service lookup:', error);
      });
  }, [sampleWeeks, selectedSampleWeekState]);

  return {
    sampleWeeks,
    selectedSampleWeek: selectedSampleWeekState,
    setSelectedSampleWeek,
    isLoadingSampleWeeks,
    loadSampleWeeks,
  };
};
